import { InputHTMLAttributes, useId, useState } from 'react';

/** Input — single-line text field with optional label + crimson error. */
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Field label (sentence case). */
  label?: string;
  /** Error message — turns the border crimson and shows the text. */
  error?: string;
  /** 'default' (sunken --bg fill, hairline border) — or 'inverted': translucent
   *  dark fill with a cream label/text/placeholder, for a fixed dark/violet
   *  backdrop that doesn't track the app's light/dark theme (e.g. auth screens). */
  tone?: 'default' | 'inverted';
}

/**
 * Input — single-line text field. Sunken (bg) fill, hairline border, gold
 * focus ring. Label is sentence case; error text is crimson.
 */
export function Input({ label, error, id: idProp, disabled, style, tone = 'default', className, ...props }: InputProps) {
  // Generate an id when the caller doesn't pass one, so `htmlFor` always has
  // a target. Without this a label with no `id` renders as a floating text
  // node and the control is unlabelled to assistive tech — which was true of
  // every field on the host profile.
  const autoId = useId();
  const id = idProp ?? autoId;
  const [focus, setFocus] = useState(false);
  const errored = !!error;
  const inverted = tone === 'inverted';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: inverted ? 3 : 6, opacity: disabled ? 0.5 : 1 }}>
      {label ? (
        <label htmlFor={id} style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-footnote)', fontWeight: 500, color: inverted ? 'rgba(255, 252, 219, .85)' : 'var(--text-soft)' }}>{label}</label>
      ) : null}
      <input
        id={id}
        disabled={disabled}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        className={inverted ? ['sk-input-inverted', className].filter(Boolean).join(' ') : className}
        style={{
          width: '100%',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-body)',
          color: inverted ? 'rgb(var(--cream-bright))' : 'var(--text)',
          background: inverted ? 'rgba(0, 0, 0, .18)' : 'var(--bg)',
          border: `1px solid ${errored ? 'rgb(var(--crimson))' : focus ? (inverted ? 'rgb(var(--cream-bright))' : 'rgb(var(--gold))') : inverted ? 'rgba(255, 252, 219, .18)' : 'var(--line)'}`,
          borderRadius: 'var(--r-field)',
          padding: '10px 12px',
          outline: 'none',
          cursor: disabled ? 'not-allowed' : 'text',
          boxShadow: focus ? `0 0 0 3px ${errored ? 'rgb(var(--crimson) / .2)' : inverted ? 'rgb(var(--cream-bright) / .2)' : 'rgb(var(--gold) / .2)'}` : 'none',
          transition: 'border-color .2s, box-shadow .2s',
          ...style,
        }}
        {...props}
      />
      {errored ? <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-caption)', color: 'rgb(var(--crimson))' }}>{error}</p> : null}
    </div>
  );
}

export default Input;
